
import { Component, Mixins, Prop, Watch } from 'vue-property-decorator';
import { BAEventModel, EventParticipantDetails } from '@/models/baEvent';
import { BAIconsMixin, MindsetDisplayMixin, RoutingMixin, VuetifyMixin } from '@/mixins';
import { DataTableHeader, DataOptions } from 'vuetify';
import { AthleteAssessmentDataModel, MindsetReportModel, ScoutingReportModel } from '@/models';

@Component
export default class BAEventParticipants extends Mixins(VuetifyMixin, BAIconsMixin, RoutingMixin, MindsetDisplayMixin){
    @Prop({ required: true }) event: BAEventModel;
    @Prop({ required: true }) participants: Array<EventParticipantDetails>;
    @Prop({ required: true }) numItems: number;
    @Prop({ required: true }) totalItems: number;
    @Prop({ required: true }) participantsTableOptions: DataOptions;
    @Prop({ type: Boolean  }) expanded;
    @Prop({ type: Boolean  }) loading;
    @Prop({ type: Boolean  }) showBARating;
    @Prop({ type: Boolean  }) viewDetails;
    @Prop({ }) status: string;

    hasExpanded: boolean = false;
    async onToggleExpandSome(num: number = 5) {
        this.participantsTableOptions.itemsPerPage = num;
        await this.onToggleExpand(num);
    }
    async onToggleExpand(num: number = -1) {
        if( num < 0 ) num = this.event.participants.length;
        if( !this.hasExpanded ) this.participantsTableOptions.itemsPerPage = num;
        this.expanded = !this.expanded;
        if( this.expanded ) this.$emit('expand')
        else this.$emit('collapse')
        this.hasExpanded = true;
    }

    onExportParticipants(){
        this.$emit('export');
    }
    
    updateParticipantsSort(options: DataOptions) {
        this.$emit('update:sort', options);

        if( this.IsEmpty(options?.sortBy) || this.IsEmpty(options?.sortDesc) ) return;
        if( options.sortBy[0] === 'none' ) return;

        const sortBy = options.sortBy[0];
        const sortDesc = options.sortDesc[0];

        if( sortBy.length > 0 && sortBy !== 'none' ) {
            if( sortBy === 'date' ) {
                const sortedParticipants = this.participants.sort((a,b) => {
                    const aVal = a.purchase? a.purchase?.date.getTime() : 0;
                    const bVal = b.purchase? b.purchase?.date.getTime() : 0;
                    return ( Number(aVal) - Number(bVal) ) * (sortDesc? -1 : 1);
                })
                this.participants = sortedParticipants;
            } else if( sortBy === 'name' ) {
                const sortedParticipants = this.participants.sort((a,b) => {
                    let sortVal = 0;
                    if( this.IsEmpty(a?.athlete?.lastName) && this.IsEmpty(b?.athlete?.lastName) ) sortVal = 0;
                    else if( this.IsEmpty(b?.athlete?.lastName) ) sortVal = 1;
                    else if( this.IsEmpty(a?.athlete?.lastName) ) sortVal = -1;
                    else if( a.athlete.lastName.toLowerCase() > b.athlete.lastName.toLowerCase() ) sortVal = 1;
                    else if( a.athlete.lastName.toLowerCase() < b.athlete.lastName.toLowerCase() ) sortVal = -1;
                    else if( this.IsEmpty(a?.athlete?.firstName) && this.IsEmpty(b?.athlete?.firstName) ) sortVal = 0;
                    else if( this.IsEmpty(b?.athlete?.firstName) ) sortVal = 1;
                    else if( this.IsEmpty(a?.athlete?.firstName) ) sortVal = -1;
                    else if( a.athlete.firstName.toLowerCase() > b.athlete.firstName.toLowerCase() ) sortVal = 1;
                    else if( a.athlete.firstName.toLowerCase() < b.athlete.firstName.toLowerCase() ) sortVal = -1;
                    return sortVal * (sortDesc? -1 : 1);
                })
                this.participants = sortedParticipants;
            } else if( sortBy === 'birthDate' ) {
                const sortedParticipants = this.participants.sort((a,b) => {
                    const aVal = new Date(a.DateOfBirth).getTime();
                    const bVal = new Date(b.DateOfBirth).getTime();
                    return ( Number(aVal) - Number(bVal) ) * (sortDesc? -1 : 1);
                })
                this.participants = sortedParticipants;
            }
        } else if( this.showBARating ) {
            const sortedParticipants = this.participants.sort((a,b) => (b.baRating?.rating - a.baRating?.rating) )
            this.participants = sortedParticipants;
        }

        this.sortBy = options.sortBy;
        this.sortDesc = options.sortDesc;
    }
    sortBy: Array<string> = ['date'];
    sortDesc: Array<boolean> = [false];
    @Watch('participantsTableOptions') updateSort(options: DataOptions, oldOptions: DataOptions): void {
        let sanitizedOptions: DataOptions = {...options};

        if( this.IsEmpty(sanitizedOptions.sortBy) ) {
            sanitizedOptions.sortBy = this.sortBy;
        } else {
            if( sanitizedOptions.sortBy[0] === 'none' ) {
                sanitizedOptions.sortBy = this.sortBy;
                sanitizedOptions.sortDesc = this.sortDesc.map(s => !s);
            }
        }

        this.updateParticipantsSort(sanitizedOptions);
    }
    participantsSearch: string = '';
    updateParticipantsSearch() {
		this.participantsTableOptions.page = 1;
		this.$emit('update:search', this.participantsSearch);
    }
    updateParticipantItemsPerPage() {
        this.$emit('itemsPerPage', this.participantsTableOptions.itemsPerPage);
    }
    updateParticipantPage() {
        this.$emit('page', this.participantsTableOptions.page);
    }
	get TotalPages(): number {
		return Math.ceil(this.totalItems / this.participantsTableOptions.itemsPerPage);
	}

    get ParticipantHeaders(): DataTableHeader<any>[] {
        const headers: DataTableHeader<any>[] = [];

        headers.push(
            {text: 'Reg Date', value: 'date', sortable: true},
            {text: 'Name', value: 'name', sortable: true},
            {text: 'Email', value: 'email', sortable: false},
            {text: 'Ticket', value: 'ticket', sortable: false},
            {text: 'Org', value: 'organization', sortable: false},
            {text: 'DoB', value: 'birthDate', sortable: true},
        );

        if( this.viewDetails ) {
            headers.push(
                {text: 'Pos', value: 'position', sortable: false},
                {text: 'Age', value: 'age', align: 'center', sortable: false},
                {text: 'Mind', value: 'mindset', width: 10, align: 'center', sortable: false},
                {text: 'Scout', value: 'scouting', width: 10, align: 'center', sortable: false},
                {text: 'Phys', value: 'physical', width: 10, align: 'center', sortable: false},
            );

            if( this.showBARating ) {
                headers.push({text: 'BAr', value: 'barating', align: 'center', sortable: false});
            }
        }
        if( this.IsAdmin ) {
            if( this.viewDetails) headers.push({text: 'ID', value: 'id', sortable: false});
            headers.push({text: '', width:"50", value: 'actions', sortable: false});
        }

        headers.push({text: '', value: 'data-table-expand', sortable: false});
        
        return headers;
    }
    get RowsPerPageItems() {
        const items = [
            { name: 'Some', value: 5 },
            { name: '50', value: 50 },
            { name: '100', value: 100 },
            { name: '250', value: 250 },
        ]
        items.push({ name: 'All', value: this.participants.length })
        return items;
    }

    GetMindsetReport(participant: EventParticipantDetails): MindsetReportModel {
        let mindsetReport = participant.baRating?.mindsetReport;
        if( this.IsEmpty(mindsetReport) ) mindsetReport = participant.recruitingProfile?.mindsetReport;
        return mindsetReport;
    }
    HasMindsetReport(participant: EventParticipantDetails): boolean {
        const mindsetReport = this.GetMindsetReport(participant);
        if( this.IsEmpty(mindsetReport) ) return false;
        return !mindsetReport.HasError;
    }
    GetMindsetArchetype(participant: EventParticipantDetails): string {
        const mindsetReport = this.GetMindsetReport(participant);
        if( this.IsEmpty(mindsetReport) ) return undefined;
        return mindsetReport.archetype;
    }

    GetScoutingReport(participant: EventParticipantDetails): ScoutingReportModel {
        let scoutingReport = participant.baRating?.scoutingReport;
        if( this.IsEmpty(scoutingReport) ) scoutingReport = participant.recruitingProfile?.scoutingReport;
        return scoutingReport;
    }
    HasScoutingReport(participant: EventParticipantDetails): boolean {
        const scoutingReport = this.GetScoutingReport(participant);
        if( this.IsEmpty(scoutingReport) ) return false;
        return scoutingReport.IsComplete;
    }

    GetPhysicalAssessment(participant: EventParticipantDetails): AthleteAssessmentDataModel {
        let physicalAssessment = participant.baRating?.physicalPerformance;
        if( this.IsEmpty(physicalAssessment) ) physicalAssessment = participant.recruitingProfile?.physicalPerformance;
        return physicalAssessment;
    }
    HasPhysicalAssessment(participant: EventParticipantDetails): boolean {
        const physicalAssessment = this.GetPhysicalAssessment(participant);
        return this.IsNotEmpty(physicalAssessment?.assessmentDate);
    }

    TicketName(ticketId: string): string {
        if( this.IsEmpty(ticketId) ) return ticketId;
        if( this.IsEmpty(this.event?.Tickets) ) return ticketId;
        const ticket = this.event?.Tickets.find(t => t.id === ticketId);
        if( this.IsEmpty(ticket) ) return this.Snip(ticketId, 12);
        return ticket.name;
    }
}
